import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Chip,
  CircularProgress, Alert, AlertTitle, FormControlLabel, Checkbox, FormGroup
} from '@mui/material';
import { ArrowBack, Save, Download, CheckCircle, Description, VideoCall } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

// =================================================================
// INICIO: Lógica de API integrada para resolver el error de compilación.
// =================================================================

import { API_BASE_URL as API_ROOT, documentosApi } from '../services/api';

const API_BASE_URL = `${API_ROOT}/api`;

// --- Interfaces para la API ---

export interface Expediente {
  id: string;
  nig?: string;
  codigo_actual?: string;
  estado?: string;
  es_activo?: boolean;
}

export interface LineaFacturaRequest {
  referencia: string;
  descripcion: string;
  cantidad: number;
  precio: number;
}

export interface GenerarDocumentoRequest {
  expediente_id: string;
  tipo_documento: string;
  lineas: LineaFacturaRequest[];
  nota_adicional?: string;
  fecha_documento?: string; // NUEVO: Campo fecha
  factura_id?: string;
  contenido_texto?: string; // NUEVO: Para documentos genéricos
  // Para solicitud de declaración por videoconferencia
  fecha_citacion?: string;
  hora_citacion?: string;
  lugar_oficina?: boolean;
  lugar_juzgados_caceres?: boolean;
}

interface DocumentoGenerado {
  id: string;
  numero_documento: string;
  nombre_archivo: string;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const expedientesApi = {
  getExpediente: async (id: string): Promise<Expediente> => {
    const response = await fetch(`${API_BASE_URL}/expedientes/${id}`, {
      headers: { ...authHeaders() },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Error al cargar el expediente' }));
      throw new Error(errorData.detail);
    }
    return response.json();
  },
  generarDocumento: async (data: GenerarDocumentoRequest): Promise<DocumentoGenerado> => {
    const response = await fetch(`${API_BASE_URL}/documentos/generar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Error al generar el documento' }));
      throw new Error(errorData.detail);
    }
    return response.json();
  }
};

// =================================================================
// FIN: Lógica de API integrada.
// =================================================================

// --- Interfaces locales del componente ---
interface LineaFactura {
  referencia: string;
  descripcion: string;
  cantidad: number;
  precio: number;
  importe: number;
}

interface GrupoFactura {
  titulo: string;
  lineas: LineaFactura[];
}

const GenerarDocumento: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const expedienteId = searchParams.get('expediente');
  const tipoDocumento = searchParams.get('tipo');
  const callback = searchParams.get('callback');
  
  const [expediente, setExpediente] = useState<Expediente | null>(null);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notaAdicional, setNotaAdicional] = useState('');
  const [documentoGenerado, setDocumentoGenerado] = useState<DocumentoGenerado | null>(null);
  
  // NUEVO: Estado para documento genérico
  const [contenidoTexto, setContenidoTexto] = useState(
      `<p><strong>EXPONE:</strong></p>
      <p>Por medio de la presente, le solicito...</p>
      <p><strong>SOLICITA:</strong></p>
      <p>Por medio de la presente, le solicito...</p>`
    );  

  // NUEVO: Estado para fecha del documento
  const [fechaDocumento, setFechaDocumento] = useState(() => {
    // Fecha actual en formato YYYY-MM-DD para el input
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
  });

  // NUEVO: Estado para solicitud de declaración por videoconferencia
  const [fechaCitacion, setFechaCitacion] = useState('');
  const [horaCitacion, setHoraCitacion] = useState('');
  const [lugarOficina, setLugarOficina] = useState(true);
  const [lugarJuzgadosCaceres, setLugarJuzgadosCaceres] = useState(false);
  
  const [grupos, setGrupos] = useState<GrupoFactura[]>([
    {
      titulo: 'Aceptación y Apertura de Expediente',
      lineas: [
        { referencia: 'ACEP-T', descripcion: 'Tiempo en aceptación y apertura de expediente', cantidad: 1, precio: 68.70, importe: 0 },
        { referencia: 'ACEP-D-H', descripcion: 'Tiempo en desplazamiento', cantidad: 0, precio: 51.79, importe: 0 },
        { referencia: 'ACEP-D-KM', descripcion: 'Km en desplazamiento', cantidad: 0, precio: 0.21, importe: 0 },
      ]
    },
    {
      titulo: 'Visita de Inspección',
      lineas: [
        { referencia: 'INSP-V-H', descripcion: 'Horas de Inspección', cantidad: 0, precio: 68.70, importe: 0 },
        { referencia: 'INSP-D-H', descripcion: 'Horas en desplazamiento', cantidad: 0, precio: 51.79, importe: 0 },
        { referencia: 'INSP-D-KM', descripcion: 'Km en desplazamiento', cantidad: 0, precio: 0.21, importe: 0 },
      ]
    },
    {
      titulo: 'Elaboración de Informe',
      lineas: [
        { referencia: 'INF-ELAB-H', descripcion: 'Elaboración de informe pericial (Horas)', cantidad: 6, precio: 68.70, importe: 0 },
      ]
    },
    {
      titulo: 'Ratificación y Entrega',
      lineas: [
        { referencia: 'INF-ENTR', descripcion: 'Entrega informe pericial', cantidad: 2, precio: 68.70, importe: 0 },
        { referencia: 'RAT-D-H', descripcion: 'Desplazamiento para ratificación dictamen (Horas)', cantidad: 0, precio: 51.79, importe: 0 },
        { referencia: 'RAT-D-KM', descripcion: 'Km desplazamiento para ratificación', cantidad: 0, precio: 0.21, importe: 0 },
      ]
    },
    {
      titulo: 'Asistencia a Juicio',
      lineas: [
        { referencia: 'JUIC-A-H', descripcion: 'Asistencia a juicio (Horas)', cantidad: 0, precio: 68.70, importe: 0 },
        { referencia: 'JUIC-D-H', descripcion: 'Desplazamiento asistencia juicio (Horas)', cantidad: 0, precio: 51.79, importe: 0 },
        { referencia: 'JUIC-D-KM', descripcion: 'Kilometraje asistencia a juicio (Km)', cantidad: 0, precio: 0.21, importe: 0 }
      ]
    }
  ]);

  useEffect(() => {
    if (!expedienteId || !tipoDocumento) {
      setError('Parámetros de expediente o tipo de documento faltantes en la URL.');
      setLoading(false);
      return;
    }

    const loadExpediente = async () => {
      try {
        setLoading(true);
        const data = await expedientesApi.getExpediente(expedienteId);
        setExpediente(data);
      } catch (err: any) {
        setError(err.message || 'Error cargando el expediente.');
        console.error("Error en loadExpediente:", err);
      } finally {
        setLoading(false);
      }
    };
    loadExpediente();
  }, [expedienteId, tipoDocumento, searchParams]);

  useEffect(() => {
    const calcularImportesIniciales = () => {
      const nuevosGrupos = grupos.map(grupo => ({
        ...grupo,
        lineas: grupo.lineas.map(linea => ({
          ...linea,
          importe: linea.cantidad * linea.precio,
        })),
      }));
      setGrupos(nuevosGrupos);
    };
    calcularImportesIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("DEBUG: El estado 'documentoGenerado' ha cambiado:", documentoGenerado);
  }, [documentoGenerado]);

  const handleCantidadChange = (groupIndex: number, lineIndex: number, nuevaCantidad: number) => {
    console.log('🔄 handleCantidadChange llamado:', { groupIndex, lineIndex, nuevaCantidad });

    // Hacer una copia profunda para asegurar que React detecte el cambio
    const nuevosGrupos = grupos.map((grupo, gIdx) => {
      if (gIdx === groupIndex) {
        return {
          ...grupo,
          lineas: grupo.lineas.map((linea, lIdx) => {
            if (lIdx === lineIndex) {
              // Si el valor es NaN (campo vacío o inválido), usar 0
              // Si es negativo, usar 0
              const cantidadValida = isNaN(nuevaCantidad) || nuevaCantidad < 0 ? 0 : nuevaCantidad;
              const nuevoImporte = cantidadValida * linea.precio;

              console.log('✅ Actualizando línea:', {
                descripcion: linea.descripcion,
                cantidadAnterior: linea.cantidad,
                cantidadNueva: cantidadValida,
                importeAnterior: linea.importe,
                importeNuevo: nuevoImporte
              });

              return {
                ...linea,
                cantidad: cantidadValida,
                importe: nuevoImporte
              };
            }
            return linea;
          })
        };
      }
      return grupo;
    });

    console.log('📊 Totales después del cambio:', {
      totalBase: nuevosGrupos.flatMap(g => g.lineas).reduce((total, linea) => total + linea.importe, 0),
      totalIVA: nuevosGrupos.flatMap(g => g.lineas).reduce((total, linea) => total + linea.importe, 0) * 0.21
    });

    setGrupos(nuevosGrupos);
  };

  const getTotalBase = () => {
    return grupos.flatMap(g => g.lineas).reduce((total, linea) => total + linea.importe, 0);
  };

  const getTotalIVA = () => getTotalBase() * 0.21;
  const getTotalFinal = () => getTotalBase() + getTotalIVA();

  const capitalizar = (texto: string) => texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : '';

  // NUEVA: Función específica para generar documento genérico
  const handleGenerarDocumentoGenerico = async () => {
    if (!expedienteId || !tipoDocumento) {
      setError("Faltan parámetros requeridos.");
      return;
    }

    if (!contenidoTexto || contenidoTexto.trim() === '' || contenidoTexto === '<p><br></p>') {
      setError("Debe escribir algún contenido para el documento genérico.");
      return;
    }

    try {
      setGenerando(true);
      setError(null);
      setDocumentoGenerado(null);

      const requestData = {
        expediente_id: expedienteId,
        tipo_documento: tipoDocumento,
        lineas: [], // Los documentos genéricos no necesitan líneas
        nota_adicional: notaAdicional,
        fecha_documento: fechaDocumento,
        contenido_texto: contenidoTexto // NUEVO: Campo para el texto libre
      };
      
      console.log("Generando documento genérico:", requestData);
      const docGenerado = await expedientesApi.generarDocumento(requestData);
      setDocumentoGenerado(docGenerado);

    } catch (err: any) {
      console.error("Error al generar documento genérico:", err);
      setError(err.message || 'Error al generar el documento genérico.');
    } finally {
      setGenerando(false);
    }
  };

  // Función específica para generar solicitud de costas (sin líneas de factura)
  const handleGenerarSolicitudCostas = async () => {
    if (!expedienteId || !tipoDocumento) {
      setError("Faltan parámetros requeridos.");
      return;
    }

    // ✅ CORREGIDO: La solicitud de costas NO requiere factura_id
    // Solo necesita el expediente_id y la fecha del documento

    try {
      setGenerando(true);
      setError(null);
      setDocumentoGenerado(null);

      const requestData = {
        expediente_id: expedienteId,
        tipo_documento: tipoDocumento,
        lineas: [], // Las costas no necesitan líneas
        nota_adicional: notaAdicional,
        fecha_documento: fechaDocumento // NUEVO: Incluir fecha
      };
      
      console.log("Generando solicitud de costas:", requestData);
      const docGenerado = await expedientesApi.generarDocumento(requestData);
      setDocumentoGenerado(docGenerado);

    } catch (err: any) {
      console.error("Error al generar solicitud de costas:", err);
      setError(err.message || 'Error al generar el documento de costas.');
    } finally {
      setGenerando(false);
    }
  };

  // Función específica para generar solicitud de declaración por videoconferencia
  const handleGenerarSolicitudVideoconferencia = async () => {
    if (!expedienteId || !tipoDocumento) {
      setError("Faltan parámetros requeridos.");
      return;
    }

    if (!fechaCitacion) {
      setError("Debe indicar la fecha de la citación para asistir al acto.");
      return;
    }

    if (!lugarOficina && !lugarJuzgadosCaceres) {
      setError("Debe marcar al menos un lugar desde el que solicitar la videoconferencia.");
      return;
    }

    try {
      setGenerando(true);
      setError(null);
      setDocumentoGenerado(null);

      const requestData: GenerarDocumentoRequest = {
        expediente_id: expedienteId,
        tipo_documento: tipoDocumento,
        lineas: [], // No necesita líneas
        nota_adicional: notaAdicional,
        fecha_documento: fechaDocumento,
        fecha_citacion: fechaCitacion,
        hora_citacion: horaCitacion,
        lugar_oficina: lugarOficina,
        lugar_juzgados_caceres: lugarJuzgadosCaceres,
      };

      console.log("Generando solicitud de videoconferencia:", requestData);
      const docGenerado = await expedientesApi.generarDocumento(requestData);
      setDocumentoGenerado(docGenerado);

    } catch (err: any) {
      console.error("Error al generar solicitud de videoconferencia:", err);
      setError(err.message || 'Error al generar la solicitud de videoconferencia.');
    } finally {
      setGenerando(false);
    }
  };

  const handleDescargarDocumento = async () => {
    if (!documentoGenerado) return;
    try {
      const blob = await documentosApi.downloadDocumento(documentoGenerado.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = documentoGenerado.nombre_archivo || 'documento';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      setError(err.message || 'Error al descargar el documento.');
    }
  };

  const handleGenerar = async () => {
    console.log("--- handleGenerar: Botón presionado ---");

    if (!expedienteId || !tipoDocumento) {
      console.error("handleGenerar: ¡ERROR! Faltan parámetros en la URL.", { expedienteId, tipoDocumento });
      setError("Falta el ID de expediente o el tipo de documento en la URL.");
      return;
    }
    console.log("handleGenerar: Parámetros validados:", { expedienteId, tipoDocumento });

    try {
      console.log("handleGenerar: Estableciendo estado 'generando' a true.");
      setGenerando(true);
      setError(null);
      setDocumentoGenerado(null);

      const lineasParaEnviar: LineaFacturaRequest[] = grupos
        .flatMap(g => g.lineas)
        .filter(linea => linea.cantidad > 0)
        .map(({ referencia, descripcion, cantidad, precio }) => ({
          referencia,
          descripcion,
          cantidad,
          precio,
        }));

      const requestData: GenerarDocumentoRequest = {
        expediente_id: expedienteId,
        tipo_documento: tipoDocumento,
        lineas: lineasParaEnviar,
        nota_adicional: notaAdicional,
        fecha_documento: fechaDocumento // NUEVO: Incluir fecha
      };
      
      console.log("handleGenerar: Enviando datos a la API...", requestData);
      const docGenerado = await expedientesApi.generarDocumento(requestData);
      console.log("handleGenerar: Éxito. Respuesta de la API:", docGenerado);
      setDocumentoGenerado(docGenerado);

    } catch (err: any) {
      console.error("handleGenerar: ¡ERROR! La llamada a la API ha fallado.", err);
      setError(err.message || 'Error al generar el documento. Revisa la consola del navegador y del servidor.');
    } finally {
      console.log("handleGenerar: Estableciendo estado 'generando' a false.");
      setGenerando(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /> <Typography ml={2}>Cargando...</Typography></Box>;
  if (!tipoDocumento) return <Alert severity="warning" sx={{ m: 3 }}>Tipo de documento no especificado.</Alert>;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(`/expedientes/${expedienteId}`)} variant="outlined">Volver</Button>
          <Typography variant="h4" component="h1">
            {tipoDocumento === 'generico' ? (
              <>
                <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                Solicitud General
              </>
            ) : tipoDocumento === 'solicitud_videoconferencia' ? (
              <>
                <VideoCall sx={{ mr: 1, verticalAlign: 'middle' }} />
                Solicitud Declaración por Videoconferencia
              </>
            ) : (
              `Generar ${capitalizar(tipoDocumento)}`
            )}
          </Typography>
        </Box>
        <Chip label={`Expediente: ${expediente?.codigo_actual || expedienteId}`} color="primary" />
      </Box>

      {/* Lógica de renderizado condicional para mostrar el éxito o el formulario */}
      {documentoGenerado ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 60, color: 'green', mb: 2 }} />
          <Typography variant="h5" gutterBottom>¡Documento generado exitosamente!</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            El documento <strong>{documentoGenerado.nombre_archivo}</strong> se ha creado correctamente.
          </Typography>
          <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDescargarDocumento}
            >
              Descargar Documento
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/expedientes/${expedienteId}`)}
            >
              Volver al Expediente
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          {/* Formulario de generación */}
          <Paper sx={{ p: 3 }}>
            {/* Campo Fecha del Documento */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>Fecha del Documento</Typography>
              <TextField
                type="date"
                value={fechaDocumento}
                onChange={(e) => setFechaDocumento(e.target.value)}
                fullWidth
                helperText="Fecha que aparecerá en el documento generado"
                sx={{ maxWidth: 300 }}
              />
            </Box>

            {/* CONDICIONAL: Editor de texto rico para documento genérico */}
            {tipoDocumento === 'generico' ? (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  📝 Contenido de la Solicitud General
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Escriba el contenido de su solicitud general. Puede usar formato de texto, listas y enlaces.
                </Typography>
                <TextField
                  multiline
                  rows={15}
                  value={contenidoTexto}
                  onChange={(e) => setContenidoTexto(e.target.value)}
                  placeholder="Escriba aquí el contenido de su solicitud general en HTML.

Ejemplo:
<p>Este es un párrafo.</p>
<p><strong>Texto en negrita</strong> y <em>texto en cursiva</em>.</p>
<ul>
  <li>Primer elemento de lista</li>
  <li>Segundo elemento de lista</li>
</ul>"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      lineHeight: 1.5
                    }
                  }}
                  helperText="Puede usar etiquetas HTML básicas: <p>, <strong>, <em>, <ul>, <li>, <br>"
                />
              </Box>
            ) : null}

            {/* CONDICIONAL: Campos para solicitud de declaración por videoconferencia */}
            {tipoDocumento === 'solicitud_videoconferencia' ? (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  🎥 Datos de la Citación
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
                  <TextField
                    type="date"
                    label="Fecha de la citación"
                    value={fechaCitacion}
                    onChange={(e) => setFechaCitacion(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Fecha del Juicio/acto para el que se solicita videoconferencia"
                    sx={{ maxWidth: 260 }}
                  />
                  <TextField
                    type="time"
                    label="Hora de la citación"
                    value={horaCitacion}
                    onChange={(e) => setHoraCitacion(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ maxWidth: 200 }}
                  />
                </Box>

                <Typography variant="h6" gutterBottom>
                  📍 Lugar desde el que se solicita
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Marque una o ambas opciones. Si marca ambas, se solicitará la oficina como opción
                  preferente y los Juzgados de Cáceres como alternativa.
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lugarOficina}
                        onChange={(e) => setLugarOficina(e.target.checked)}
                      />
                    }
                    label="Desde mi oficina"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lugarJuzgadosCaceres}
                        onChange={(e) => setLugarJuzgadosCaceres(e.target.checked)}
                      />
                    }
                    label="Desde los Juzgados de Cáceres"
                  />
                </FormGroup>
              </Box>
            ) : null}

            {/* Nota Adicional */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>Nota Adicional</Typography>
              <TextField
                multiline
                rows={3}
                value={notaAdicional}
                onChange={(e) => setNotaAdicional(e.target.value)}
                placeholder="Nota adicional para el documento..."
                fullWidth
              />
            </Box>

            {/* Tabla de líneas (solo para documentos que no sean solicitud de costas, genérico o videoconferencia) */}
            {tipoDocumento !== 'solicitud_costas' && tipoDocumento !== 'generico' && tipoDocumento !== 'solicitud_videoconferencia' && (
              <>
                <Typography variant="h6" gutterBottom>Conceptos de Facturación</Typography>
                {grupos.map((grupo, groupIndex) => (
                  <Box key={groupIndex} mb={3}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={2} color="primary">
                      {grupo.titulo}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Descripción</TableCell>
                            <TableCell align="center">Cantidad</TableCell>
                            <TableCell align="right">Precio</TableCell>
                            <TableCell align="right">Importe</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {grupo.lineas.map((linea, lineIndex) => (
                            <TableRow key={`${groupIndex}-${lineIndex}-${linea.referencia}`}>
                              <TableCell>{linea.descripcion}</TableCell>
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  value={linea.cantidad}
                                  onChange={(e) => {
                                    const valor = e.target.value;
                                    const numero = valor === '' ? 0 : parseFloat(valor);
                                    handleCantidadChange(groupIndex, lineIndex, numero);
                                  }}
                                  size="small"
                                  sx={{ width: 80 }}
                                  inputProps={{ step: 0.1, min: 0 }}
                                />
                              </TableCell>
                              <TableCell align="right">{linea.precio.toFixed(2)}€</TableCell>
                              <TableCell align="right">{linea.importe.toFixed(2)}€</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}

                {/* Resumen de totales */}
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Resumen</Typography>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>Total Base:</Typography>
                    <Typography>{getTotalBase().toFixed(2)}€</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>IVA (21%):</Typography>
                    <Typography>{getTotalIVA().toFixed(2)}€</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" fontWeight="bold">
                    <Typography fontWeight="bold">Total Final:</Typography>
                    <Typography fontWeight="bold">{getTotalFinal().toFixed(2)}€</Typography>
                  </Box>
                </Paper>
              </>
            )}

            {/* Botón de generar - CONDICIONAL según el tipo */}
            <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate(`/expedientes/${expedienteId}`)}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={
                  tipoDocumento === 'solicitud_costas'
                    ? handleGenerarSolicitudCostas
                    : tipoDocumento === 'generico'
                    ? handleGenerarDocumentoGenerico
                    : tipoDocumento === 'solicitud_videoconferencia'
                    ? handleGenerarSolicitudVideoconferencia
                    : handleGenerar
                }
                disabled={generando}
                startIcon={generando ? <CircularProgress size={20} /> : <Save />}
              >
                {generando ? 'Generando...' :
                 tipoDocumento === 'generico' ? 'Generar Solicitud General' :
                 tipoDocumento === 'solicitud_videoconferencia' ? 'Generar Solicitud de Videoconferencia' :
                 `Generar ${capitalizar(tipoDocumento)}`}
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default GenerarDocumento;